import React, {
useEffect,
useMemo,
useState,
} from "react";

import api from "../../api/api";
import Swal from "sweetalert2";

import {
Card,
Row,
Col,
Typography,
Select,
Button,
Space,
Statistic,
Divider,
Alert,
Spin,
} from "antd";

import {
PrinterOutlined,
BookOutlined,
ReadOutlined,
CalendarOutlined,
FilePdfOutlined,
} from "@ant-design/icons";

import {
PDFDownloadLink,
} from "@react-pdf/renderer";

import TestPDF from "./TestPDF";

import { useAcademicYear } from "../../AcademicYearContext";

const extractList = data =>
Array.isArray(data)
? data
: Array.isArray(data?.results)
? data.results
: [];

const PrintTestByClass = () => {
const [
tests,
setTests,
] = useState([]);

const [
loadingTests,
setLoadingTests,
] = useState(true);

const [
selectedGradeClass,
setSelectedGradeClass,
] = useState("");

const [
selectedSubject,
setSelectedSubject,
] = useState("");

const [
selectedPeriod,
setSelectedPeriod,
] = useState("");

const [
printing,
setPrinting,
] = useState(false);

const [
preparedTest,
setPreparedTest,
] = useState(null);

const {
selectedYear,
loading: loadingAcademicYear,
} = useAcademicYear();

const token =
localStorage.getItem(
"access"
);

const authHeaders = {
Authorization: `Bearer ${token}`,
};

/*

* =====================================================
* FETCH TESTS
* =====================================================
  */

useEffect(() => {
let mounted = true;


const fetchTests = async () => {
  if (loadingAcademicYear) {
    return;
  }

  if (!selectedYear?.id) {
    if (mounted) {
      setTests([]);
      setLoadingTests(false);
    }

    return;
  }

  if (mounted) {
    setLoadingTests(true);
  }

  try {
const response = await api.get("/api/periodic-tests/", {
    headers: authHeaders,
    params: {
        academic_year: selectedYear.id,
    },
});

console.log("API RESPONSE:", response.data);

const testList = extractList(response.data);

console.log("TEST LIST:", testList);

if (mounted) {
    setTests(testList);
}
  } catch (error) {
    console.error(
      "Failed to load tests:",
      error
    );

    if (mounted) {
      setTests([]);

      Swal.fire({
        icon: "error",
        title: "Failed to Load Tests",
        text: "The tests could not be loaded.",
        confirmButtonColor: "#800000",
      });
    }
  } finally {
    if (mounted) {
      setLoadingTests(false);
    }
  }
};

fetchTests();

return () => {
  mounted = false;
};


}, [
selectedYear?.id,
loadingAcademicYear,
]);

/*

* =====================================================
* GRADE CLASS OPTIONS
* =====================================================
  */

const gradeClassOptions =
useMemo(() => {
const map = new Map();


  for (const test of tests) {
    const gradeClassId =
    test.grade_class
      // test.grade_class_id ??
      // test.grade_class;

    const gradeClassName =
      test.grade_class_name ??
      `Grade Class ${gradeClassId}`;

    if (!gradeClassId) {
      continue;
    }

    if (map.has(gradeClassId)) {
      continue;
    }

    map.set(
      gradeClassId,
      {
        id: gradeClassId,
        name: gradeClassName,
      }
    );
  }

  return [
    ...map.values(),
  ].sort(
    (a, b) =>
      a.name.localeCompare(
        b.name
      )
  );
}, [tests]);


/*

* =====================================================
* SUBJECT OPTIONS
* =====================================================
  */

const subjectOptions =
useMemo(() => {
if (!selectedGradeClass) {
return [];
}


  const map = new Map();

  for (const test of tests) {
    const gradeClassId =
    test.grade_class
      // test.grade_class_id ??
      // test.grade_class;

    if (
      String(
        gradeClassId
      ) !==
      String(
        selectedGradeClass
      )
    ) {
      continue;
    }

    const subjectId =
    test.subject
      // test.subject_id ??
      // test.subject;

    const subjectName =
      test.subject_name ??
      `Subject ${subjectId}`;

    if (!subjectId) {
      continue;
    }

    if (map.has(subjectId)) {
      continue;
    }

    map.set(
      subjectId,
      {
        id: subjectId,
        name: subjectName,
      }
    );
  }

  return [
    ...map.values(),
  ].sort(
    (a, b) =>
      a.name.localeCompare(
        b.name
      )
  );
}, [
  tests,
  selectedGradeClass,
]);


/*

* =====================================================
* PERIOD OPTIONS
* =====================================================
  */

const periodOptions =
useMemo(() => {
if (
!selectedGradeClass ||
!selectedSubject
) {
return [];
}


  const map = new Map();

  for (const test of tests) {
    const gradeClassId =
    test.grade_class
      // test.grade_class_id ??
      // test.grade_class;

    const subjectId =
    test.subject
      // test.subject_id ??
      // test.subject;

    if (
      String(
        gradeClassId
      ) !==
        String(
          selectedGradeClass
        ) ||
      String(
        subjectId
      ) !==
        String(
          selectedSubject
        )
    ) {
      continue;
    }

    const periodId =
    test.period
      // test.period_id ??
      // test.period;

    const periodName =
      test.period_name ??
      `Period ${periodId}`;

    if (!periodId) {
      continue;
    }

    if (map.has(periodId)) {
      continue;
    }

    map.set(
      periodId,
      {
        id: periodId,
        name: periodName,
      }
    );
  }

  return [
    ...map.values(),
  ].sort(
    (a, b) =>
      a.name.localeCompare(
        b.name
      )
  );
}, [
  tests,
  selectedGradeClass,
  selectedSubject,
]);


/*

* =====================================================
* FIND SELECTED TEST
* =====================================================
  */

const findMatchingTestSummary =
() => {
return tests.find(test => {
const gradeClassId =
test.grade_class
// test.grade_class_id ??
// test.grade_class;


    const subjectId =
    test.subject
      // test.subject_id ??
      // test.subject;

    const periodId =
    test.period
      // test.period_id ??
      // test.period;

    return (
      String(
        gradeClassId
      ) ===
        String(
          selectedGradeClass
        ) &&
      String(
        subjectId
      ) ===
        String(
          selectedSubject
        ) &&
      String(
        periodId
      ) ===
        String(
          selectedPeriod
        )
    );
  });
};


/*

* =====================================================
* FETCH TEST DETAIL
* =====================================================
  */

const fetchTestDetail =
async testId => {
const response =
await api.get(
`/api/periodic-tests/${testId}/`,
{
headers: authHeaders,
}
);


  return response.data;
};


/*

* =====================================================
* FETCH ENROLLED STUDENTS
* =====================================================
  */

const fetchEnrolledStudents =
async gradeClassId => {
if (!selectedYear?.id) {
return [];
}


  const response =
    await api.get(
      "/api/users/students/",
      {
        headers: authHeaders,
        params: {
          grade_class:
            gradeClassId,
          status: "Enrolled",
          academic_year:
            selectedYear.id,
        },
      }
    );

  return extractList(
    response.data
  );
};


/*

* =====================================================
* PREPARE STUDENT TESTS
* =====================================================
  */

const prepareStudentTests =
(
test,
students
) => {
return students.map(
student => ({
...test,


      student_id:
        student.id,

      student_name:
        student.full_name,

      full_name:
        student.full_name,

      student_admission:
        student.admission_number,

      student_admission_number:
        student.admission_number,
    })
  );
};


/*

* =====================================================
* PREPARE TEST PDF DATA
* =====================================================
  */

const generateTestPDF =
async () => {
if (
!selectedGradeClass ||
!selectedSubject ||
!selectedPeriod
) {
await Swal.fire({
icon: "warning",
title: "Selection Required",
text: "Please select Grade Class, Subject, and Period.",
confirmButtonColor: "#800000",
});


    return;
  }

  const summary =
    findMatchingTestSummary();

  if (!summary) {
    await Swal.fire({
      icon: "info",
      title: "No Test Found",
      text: "No test was found for the selected Grade Class, Subject, and Period.",
      confirmButtonColor: "#800000",
    });

    return;
  }

  setPrinting(true);

  try {
    const test =
      await fetchTestDetail(
        summary.id
      );

    const students =
      await fetchEnrolledStudents(
        selectedGradeClass
      );

    if (!students.length) {
      await Swal.fire({
        icon: "info",
        title: "No Students Found",
        text: "No enrolled students were found in the selected Grade Class.",
        confirmButtonColor: "#800000",
      });

      return;
    }

    const studentTests =
      prepareStudentTests(
        test,
        students
      );

    setPreparedTest({
      test,
      students,
      studentTests,
    });

    await Swal.fire({
      icon: "success",
      title: "Test Papers Prepared",
      text: `${students.length} student test paper(s) are ready for download.`,
      confirmButtonColor: "#800000",
      timer: 2500,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error(
      "Failed to prepare test PDF:",
      error
    );

    setPreparedTest(null);

    await Swal.fire({
      icon: "error",
      title: "Preparation Failed",
      text:
        error?.response?.data?.detail ||
        "The test papers could not be prepared.",
      confirmButtonColor: "#800000",
    });
  } finally {
    setPrinting(false);
  }
};


/*

* =====================================================
* PDF FILE NAME
* =====================================================
  */

const pdfFileName =
`test_${(
      selectedSubject ||
      "subject"
    ).toString()}_${
      selectedGradeClass ||
      "class"
    }_${
      selectedPeriod ||
      "period"
    }_students.pdf`;

const {
Title,
Text,
} = Typography;

const disabled =
loadingTests ||
loadingAcademicYear ||
printing ||
!selectedGradeClass ||
!selectedSubject ||
!selectedPeriod;

return (
<div
style={{
padding: 24,
background:
"transparent",
minHeight:
"100vh",
}}
>
<Card
bordered={false}
style={{
borderRadius: 18,
boxShadow:
"0 8px 30px rgba(0,0,0,.08)",
}}
>
<Space
direction="vertical"
size={2}
style={{
width: "100%",
marginBottom: 25,
}}
>
<Title
level={3}
style={{
margin: 0,
color: "#800000",
display:
"flex",
alignItems:
"center",
gap: 10,
}}
> <PrinterOutlined />


        Print Test By Class
      </Title>

      <Text type="secondary">
        Generate professionally formatted examination papers for every enrolled student.
      </Text>

      <Text type="secondary">
        Active Academic Year:{" "}

        <strong>
          {loadingAcademicYear
            ? "Loading..."
            : selectedYear?.name ||
              "No Active Year"}
        </strong>
      </Text>
    </Space>

    <Row
      gutter={16}
      style={{
        marginBottom: 25,
      }}
    >
      <Col
        xs={24}
        md={8}
      >
        <Card
          bordered={false}
          style={{
            background:
              "#fafafa",
          }}
        >
          <Statistic
            title="Available Tests"
            value={
              tests.length
            }
            prefix={
              <BookOutlined
                style={{
                  color:
                    "#800000",
                }}
              />
            }
          />
        </Card>
      </Col>

      <Col
        xs={24}
        md={8}
      >
        <Card
          bordered={false}
          style={{
            background:
              "#fafafa",
          }}
        >
          <Statistic
            title="Grade Classes"
            value={
              gradeClassOptions.length
            }
            prefix={
              <ReadOutlined
                style={{
                  color:
                    "#800000",
                }}
              />
            }
          />
        </Card>
      </Col>

      <Col
        xs={24}
        md={8}
      >
        <Card
          bordered={false}
          style={{
            background:
              "#fafafa",
          }}
        >
          <Statistic
            title="Periods"
            value={
              periodOptions.length
            }
            prefix={
              <CalendarOutlined
                style={{
                  color:
                    "#800000",
                }}
              />
            }
          />
        </Card>
      </Col>
    </Row>

    <Divider orientation="left">
      Select Examination
    </Divider>

    {!loadingAcademicYear &&
      !loadingTests &&
      !tests.length && (
        <Alert
          type="warning"
          showIcon
          style={{
            marginBottom: 20,
          }}
          message="No Tests Available"
          description={`No tests were found for the active academic year: ${
            selectedYear?.name ||
            "Unknown"
          }.`}
        />
      )}

    <Row
      gutter={[
        20,
        20,
      ]}
    >
      <Col
        xs={24}
        md={8}
      >
        <Text strong>
          Grade Class
        </Text>

        <Select
          showSearch
          loading={
            loadingTests ||
            loadingAcademicYear
          }
          style={{
            width:
              "100%",
            marginTop: 6,
          }}
          placeholder="Select Grade Class"
          value={
            selectedGradeClass ||
            undefined
          }
          optionFilterProp="label"
          options={gradeClassOptions.map(
            gradeClass => ({
              value:
                gradeClass.id,
              label:
                gradeClass.name,
            })
          )}
          onChange={value => {
            setSelectedGradeClass(
              value
            );

            setSelectedSubject(
              ""
            );

            setSelectedPeriod(
              ""
            );

            setPreparedTest(
              null
            );
          }}
        />
      </Col>

      <Col
        xs={24}
        md={8}
      >
        <Text strong>
          Subject
        </Text>

        <Select
          showSearch
          loading={
            loadingTests ||
            loadingAcademicYear
          }
          style={{
            width:
              "100%",
            marginTop: 6,
          }}
          placeholder="Select Subject"
          value={
            selectedSubject ||
            undefined
          }
          disabled={
            !selectedGradeClass
          }
          optionFilterProp="label"
          options={subjectOptions.map(
            subject => ({
              value:
                subject.id,
              label:
                subject.name,
            })
          )}
          onChange={value => {
            setSelectedSubject(
              value
            );

            setSelectedPeriod(
              ""
            );

            setPreparedTest(
              null
            );
          }}
        />
      </Col>

      <Col
        xs={24}
        md={8}
      >
        <Text strong>
          Period
        </Text>

        <Select
          showSearch
          loading={
            loadingTests ||
            loadingAcademicYear
          }
          style={{
            width:
              "100%",
            marginTop: 6,
          }}
          placeholder="Select Period"
          value={
            selectedPeriod ||
            undefined
          }
          disabled={
            !selectedSubject
          }
          optionFilterProp="label"
          options={periodOptions.map(
            period => ({
              value:
                period.id,
              label:
                period.name,
            })
          )}
          onChange={value => {
            setSelectedPeriod(
              value
            );

            setPreparedTest(
              null
            );
          }}
        />
      </Col>
    </Row>

    {selectedGradeClass &&
      selectedSubject &&
      selectedPeriod && (
        <Alert
          style={{
            marginTop: 25,
            borderRadius: 12,
          }}
          type="info"
          showIcon
          message={
            preparedTest
              ? "Test Papers Ready"
              : "Ready to Prepare"
          }
          description={
            preparedTest
              ? `${preparedTest.students.length} student test paper(s) are ready. Click the download button below.`
              : "Click Prepare Test Papers to load all enrolled students in the selected Grade Class."
          }
        />
      )}

    <div
      style={{
        marginTop: 35,
        textAlign:
          "center",
      }}
    >
      {!preparedTest ? (
        <Button
          type="primary"
          size="large"
          icon={
            printing ? (
              <Spin
                size="small"
              />
            ) : (
              <FilePdfOutlined />
            )
          }
          loading={
            printing
          }
          disabled={
            disabled
          }
          onClick={
            generateTestPDF
          }
          style={{
            height: 55,
            padding:
              "0 45px",
            fontSize: 17,
            fontWeight:
              700,
            color:
              "#FFFFFF",
            background:
              "#000000",
            border:
              "2px solid #FFD700",
            borderRadius:
              12,
            boxShadow:
              "0 8px 20px rgba(0,0,0,.35)",
            transition:
              "all 0.3s ease-in-out",
          }}
          onMouseEnter={event => {
            event.currentTarget.style.background =
              "#8B2500";

            event.currentTarget.style.color =
              "#FFFFFF";

            event.currentTarget.style.borderColor =
              "#FFD700";

            event.currentTarget.style.boxShadow =
              "0 10px 25px rgba(139,37,0,.5)";

            event.currentTarget.style.transform =
              "translateY(-2px)";
          }}
          onMouseLeave={event => {
            event.currentTarget.style.background =
              "#000000";

            event.currentTarget.style.color =
              "#FFFFFF";

            event.currentTarget.style.borderColor =
              "#FFD700";

            event.currentTarget.style.boxShadow =
              "0 8px 20px rgba(0,0,0,.35)";

            event.currentTarget.style.transform =
              "translateY(0)";
          }}
        >
          Prepare Test Papers
        </Button>
      ) : (
        <PDFDownloadLink
          document={
            <TestPDF
              test={
                preparedTest.test
              }
              students={
                preparedTest.students
              }
              studentTests={
                preparedTest.studentTests
              }
            />
          }
          fileName={
            pdfFileName
          }
          style={{
            textDecoration:
              "none",
          }}
        >
          {({
            loading,
          }) => (
            <Button
              type="primary"
              size="large"
              icon={
                loading ? (
                  <Spin
                    size="small"
                  />
                ) : (
                  <FilePdfOutlined />
                )
              }
              loading={
                loading
              }
              style={{
                height: 55,
                padding:
                  "0 45px",
                fontSize: 17,
                fontWeight:
                  700,
                color:
                  "#FFFFFF",
                background:
                  "linear-gradient(135deg,#800000,#b22222)",
                border:
                  "none",
                borderRadius:
                  12,
                boxShadow:
                  "0 8px 20px rgba(128,0,0,.35)",
              }}
            >
              Download Test PDF
            </Button>
          )}
        </PDFDownloadLink>
      )}
    </div>
  </Card>
</div>


);
};

export default PrintTestByClass;
